import { paymentAPI } from '../services/api';
import {
  clearPendingRazorpayVerify,
  savePendingRazorpayCheckout,
  savePendingRazorpayResponse
} from './razorpayPendingVerify.js';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** The webhook may finish after the browser verify request. Never treats an open order as paid. */
async function waitForPaymentFinalization(bookingId) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    await delay(1500);
    try {
      const { data } = await paymentAPI.getBookingPaymentStatus(bookingId);
      if (data?.data?.paymentAttempt?.status === 'completed') return true;
    } catch (_) {
      // The normal verify error is retained if polling cannot reach the API.
    }
  }
  return false;
}

export async function openRazorpayBookingPayment({ booking, paymentFor = 'advance', onSuccess }) {
  if (!booking?.id) throw new Error('Booking not found for payment.');

  await loadRazorpayScript();

  const [{ data: cfgRes }, { data: orderRes }] = await Promise.all([
    paymentAPI.getConfig(),
    paymentAPI.createBookingOrder(booking.id, paymentFor)
  ]);
  const razorpayKey = cfgRes?.data?.razorpayKey;
  const order = orderRes?.data;
  if (!razorpayKey) throw new Error('Razorpay key not configured.');
  if (!order?.orderId) throw new Error('Failed to create payment order.');

  savePendingRazorpayCheckout({
    bookingId: booking.id,
    paymentFor,
    orderId: order.orderId
  });

  return new Promise((resolve, reject) => {
    const baseAmount = Number(order.amount || 0);
    const gatewayCharge = Number(order.gatewayCharge || 0);
    const payableAmount = Number(order.payableAmount || (baseAmount + gatewayCharge) || 0);
    const options = {
      key: razorpayKey,
      amount: Math.round(payableAmount * 100),
      currency: order.currency || 'INR',
      name: 'Invincible',
      description: order.description
        || (paymentFor === 'remaining'
          ? `Booking payment (+2% PG: ₹${gatewayCharge.toFixed(2)})`
          : `Advance booking payment (+2% PG: ₹${gatewayCharge.toFixed(2)})`),
      order_id: order.orderId,
      prefill: {
        name: booking?.travelers?.[0]
          ? `${booking.travelers[0].firstName || ''} ${booking.travelers[0].lastName || ''}`.trim()
          : '',
        contact: booking?.travelers?.[0]?.phone || ''
      },
      theme: {
        color: '#de5824'
      },
      handler: async (response) => {
        try {
          savePendingRazorpayResponse(response);
          const verifyOnce = () =>
            paymentAPI.verifyBookingPayment(booking.id, {
              ...response,
              paymentFor
            });
          try {
            await verifyOnce();
          } catch (firstErr) {
            // Brief retry — UPI often returns before our API is reachable again.
            await new Promise((r) => setTimeout(r, 1200));
            try {
              await verifyOnce();
            } catch (secondErr) {
              // Give the server webhook a short window to persist the captured payment.
              if (!(await waitForPaymentFinalization(booking.id))) throw secondErr;
            }
          }
          clearPendingRazorpayVerify();
          if (typeof onSuccess === 'function') await onSuccess(response);
          resolve(response);
        } catch (err) {
          // Keep pending verify in sessionStorage so login/refresh can finish recording.
          const msg = String(err?.response?.data?.message || err?.message || 'Payment verify failed');
          reject(
            new Error(
              `${msg} Payment may already be captured at Razorpay — refresh or ask admin to recover payment ${response?.razorpay_payment_id || ''}.`.trim()
            )
          );
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled by user.'))
      }
    };

    const instance = new window.Razorpay(options);
    instance.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed.'));
    });
    instance.open();
  });
}
